import React from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GavelIcon from '@mui/icons-material/Gavel';
import DescriptionIcon from '@mui/icons-material/Description';
import PhoneIcon from '@mui/icons-material/Phone';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import AssessmentIcon from '@mui/icons-material/Assessment';

const Services = () => {
  const theme = useTheme();

  const services = [
    {
      title: 'Legal Consultation',
      icon: <PhoneIcon />,
      description: 'Expert advice on legal matters and case evaluation',
      features: [
        'Initial case assessment',
        'Legal strategy consultation',
        'Document review',
        'Risk analysis',
      ],
    },
    {
      title: 'Court Representation',
      icon: <GavelIcon />,
      description: 'Professional representation in court proceedings',
      features: [
        'Hearing representation',
        'Argument presentation',
        'Evidence management',
        'Appeal filing',
      ],
    },
    {
      title: 'Document Preparation',
      icon: <DescriptionIcon />,
      description: 'Drafting and review of legal documents',
      features: [
        'Contract drafting',
        'Legal notices',
        'Petitions and applications',
        'Agreement review',
      ],
    },
    {
      title: 'Mediation & Negotiation',
      icon: <MeetingRoomIcon />,
      description: 'Alternative dispute resolution services',
      features: [
        'Settlement negotiations',
        'Mediation services',
        'Arbitration support',
        'Conflict resolution',
      ],
    },
    {
      title: 'Legal Research',
      icon: <AssessmentIcon />,
      description: 'Comprehensive legal research and analysis',
      features: [
        'Case law research',
        'Statute analysis',
        'Precedent identification',
        'Legal opinion drafting',
      ],
    },
  ];

  return (
    <Container sx={{ py: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" gutterBottom>
          Our Services
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
          Comprehensive legal services tailored to meet your needs
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {services.map((service, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Paper
              sx={{
                p: 4,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': { transform: 'translateY(-5px)', boxShadow: 4 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    p: 2,
                    borderRadius: 2,
                    mr: 2,
                  }}
                >
                  {service.icon}
                </Box>
                <Typography variant="h5">{service.title}</Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" paragraph>
                {service.description}
              </Typography>
              <List sx={{ mb: 2 }}>
                {service.features.map((feature, idx) => (
                  <ListItem key={idx} disablePadding>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>
              <Box sx={{ mt: 'auto', textAlign: 'right' }}>
                <Button
                  variant="outlined"
                  size="small"
                  component={Link}
                  to="/appointment"
                  state={{ service: service.title }}
                >
                  Book this service
                </Button>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 8, textAlign: 'center', bgcolor: 'background.paper', p: 4, borderRadius: 2 }}>
        <Typography variant="h5" gutterBottom>
          Need a Custom Solution?
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          We understand that every legal situation is unique. Contact us to discuss
          how we can tailor our services to meet your specific requirements.
        </Typography>
      </Box>
    </Container>
  );
};

export default Services;

